/** Primitivos visuales Mesas (proto mesas-v2, lenguaje Cocina). */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import {
  mesaVisualStateMeta,
  type MesaVisualState,
  type MesaVisualTone,
} from '../domain/mesaVisualStatus';

const toneColors: Record<MesaVisualTone, { bg: string; fg: string }> = {
  teal:   { bg: '#E8F7F4', fg: '#115E59' },
  info:   { bg: '#E8F0FE', fg: '#1D4ED8' },
  warm:   { bg: '#FDF0E9', fg: '#D97745' },
  ink:    { bg: '#1C1917', fg: '#FFFFFF' },
  violet: { bg: '#F1EBFB', fg: '#7C3AED' },
  danger: { bg: '#FDE8E8', fg: '#B91C1C' },
  muted:  { bg: '#EAF3F0', fg: '#6F6A65' },
};

/** Color de acento por estado derivado (dots del mapa, bordes). */
export function mesaStateAccent(state: MesaVisualState): string {
  return toneColors[mesaVisualStateMeta(state).tone].fg;
}

export function StatePill({ state, testID }: { state: MesaVisualState; testID?: string }) {
  const meta = mesaVisualStateMeta(state);
  const c = toneColors[meta.tone];
  return (
    <View testID={testID} style={[styles.pill, { backgroundColor: c.bg }]}>
      <Text style={[styles.pillLabel, { color: c.fg }]}>{meta.label}</Text>
    </View>
  );
}

export type MiniBadgeKind = 'kitchen' | 'ready' | 'signal' | 'unpaid' | 'liberate' | 'payqr';
const badgeTones: Record<MiniBadgeKind, { bg: string; fg: string; label?: never }> = {
  kitchen:  { bg: '#FDF0E9', fg: '#D97745' },
  ready:    { bg: '#E8F7F4', fg: '#115E59' },
  signal:   { bg: '#FDF0E9', fg: '#D97745' },
  unpaid:   { bg: '#FFF3E0', fg: '#B45309' },
  liberate: { bg: '#FDE8E8', fg: '#B91C1C' },
  payqr:    { bg: '#FFF3E0', fg: '#B45309' },
};
export function MiniBadge({ kind, label, pulse }: { kind: MiniBadgeKind; label: string; pulse?: boolean }) {
  const c = badgeTones[kind];
  return (
    <View style={[styles.mini, { backgroundColor: c.bg }, pulse && styles.pulse]}>
      <Text style={[styles.miniLabel, { color: c.fg }]}>{label}</Text>
    </View>
  );
}

export function KpiTile({ value, label, tone = 'ink', active, onPress, testID }: {
  value: number | string; label: string;
  tone?: 'ink' | 'warn' | 'teal' | 'violet' | 'info';
  active?: boolean; onPress?: () => void; testID?: string;
}) {
  const color =
    tone === 'warn' ? '#B45309' : tone === 'teal' ? '#115E59'
    : tone === 'violet' ? '#7C3AED' : tone === 'info' ? '#1D4ED8' : '#1C1917';
  return (
    <Pressable testID={testID} onPress={onPress}
      style={[styles.kpi, active && styles.kpiActive]}>
      <Text style={[styles.kpiNum, { color }]}>{value}</Text>
      <Text style={styles.kpiLbl}>{label}</Text>
    </Pressable>
  );
}

export function Stepper({ value, min = 1, max = 99, disabled, onChange, testID }: {
  value: number; min?: number; max?: number; disabled?: boolean;
  onChange: (next: number) => void; testID?: string;
}) {
  const bump = (d: number) => {
    const next = Math.max(min, Math.min(max, value + d));
    if (!disabled && next !== value) onChange(next);
  };
  return (
    <View style={styles.stepper} testID={testID}>
      <Pressable style={styles.stepBtn} disabled={disabled} onPress={() => bump(-1)}>
        <Text style={styles.stepBtnLabel}>−</Text>
      </Pressable>
      <Text style={styles.stepNum}>{value}</Text>
      <Pressable style={styles.stepBtn} disabled={disabled} onPress={() => bump(1)}>
        <Text style={styles.stepBtnLabel}>+</Text>
      </Pressable>
    </View>
  );
}

export function InfoBlock({ title, count, action, children }: {
  title: string; count?: number | string;
  action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <View style={styles.blk}>
      <View style={styles.blkHd}>
        <Text style={styles.blkTitle}>{title}</Text>
        {count != null ? <Text style={styles.blkCount}>{count}</Text> : null}
        {action}
      </View>
      {children}
    </View>
  );
}

export function SigStrip({ label, actionLabel, onAction, testID }: {
  label: string; actionLabel: string; onAction: () => void; testID?: string;
}) {
  return (
    <View style={styles.sig} testID={testID}>
      <Text style={styles.sigLabel}>{label}</Text>
      <Pressable style={styles.sigBtn} onPress={onAction}>
        <Text style={styles.sigBtnLabel}>{actionLabel}</Text>
      </Pressable>
    </View>
  );
}

export function CardKebabButton({ onPress, testID }: { onPress: () => void; testID?: string }) {
  return (
    <Pressable testID={testID} onPress={onPress} hitSlop={8} style={styles.kebab}>
      <Text style={styles.kebabLabel}>⋮</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pill: { borderRadius: 999, paddingHorizontal: 9, paddingVertical: 3, alignSelf: 'flex-start' },
  pillLabel: { fontSize: 10.5, fontWeight: '700', letterSpacing: 0.2 },
  mini: { borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2.5 },
  miniLabel: { fontSize: 10, fontWeight: '700' },
  pulse: { opacity: 0.9 },
  kpi: {
    backgroundColor: ticketColors.paper, borderWidth: 1, borderColor: 'rgba(28,25,23,0.09)',
    borderRadius: 14, paddingHorizontal: spacing.md, paddingVertical: 12,
  },
  kpiActive: { borderColor: '#0D9488' },
  kpiNum: { fontSize: 24, fontWeight: '700', fontVariant: ['tabular-nums'] },
  kpiLbl: { fontSize: 11.5, color: '#6F6A65', marginTop: 5, textTransform: 'uppercase', letterSpacing: 0.5 },
  stepper: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepBtn: {
    width: 26, height: 26, borderRadius: 8, borderWidth: 1,
    borderColor: 'rgba(28,25,23,0.16)', backgroundColor: ticketColors.paper,
    alignItems: 'center', justifyContent: 'center',
  },
  stepBtnLabel: { fontSize: 14, fontWeight: '700' },
  stepNum: { minWidth: 18, textAlign: 'center', fontWeight: '700', fontVariant: ['tabular-nums'] },
  blk: { borderWidth: 1, borderColor: 'rgba(28,25,23,0.09)', borderRadius: 12, overflow: 'hidden' },
  blkHd: {
    paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#FAFBFA',
    borderBottomWidth: 1, borderBottomColor: 'rgba(28,25,23,0.09)',
    flexDirection: 'row', alignItems: 'center', gap: 8,
  },
  blkTitle: { fontSize: 11, fontWeight: '700', color: '#57534E', textTransform: 'uppercase', letterSpacing: 0.5 },
  blkCount: { marginLeft: 'auto', fontSize: 11, color: '#6F6A65', fontVariant: ['tabular-nums'] },
  sig: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#FDF0E9', borderWidth: 1, borderColor: '#F5D9C4',
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 9,
  },
  sigLabel: { fontSize: 12.5, fontWeight: '600', color: '#D97745', flex: 1 },
  sigBtn: {
    borderWidth: 1, borderColor: '#F0CBAF', backgroundColor: '#FFFFFF',
    borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5,
  },
  sigBtnLabel: { fontSize: 11.5, fontWeight: '700', color: '#D97745' },
  kebab: {
    position: 'absolute', top: 8, right: 8, width: 24, height: 24,
    borderRadius: 7, alignItems: 'center', justifyContent: 'center',
  },
  kebabLabel: { fontSize: 15, color: '#6F6A65' },
});
